import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { Breadcrumb } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { VIPPS_ENVIRONMENTS } from "@/lib/vippsConstants";
import { formatDateTime, copyText } from "@/lib/format";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { Loader2, PlugZap, Webhook, Trash2, CheckCircle2, AlertTriangle, Info, ShieldCheck, Copy, ChevronDown, KeyRound, Eye, EyeOff, Smartphone, QrCode, Mail } from "lucide-react";

const EMPTY = { enabled: false, environment: "test", client_id: "", client_secret: "", subscription_key: "", msn: "", api_base_url_override: "" };

/** Passordfelt for nøkler: tomt felt = behold lagret verdi. */
const SecretInput = ({ id, label, value, onChange, isSet, hint, testId }) => {
  const [show, setShow] = useState(false);
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <Label htmlFor={id}>{label}</Label>
        {isSet ? <span className="inline-flex items-center gap-1 text-[11px] text-success" data-testid={`${testId}-saved`}><ShieldCheck className="h-3 w-3" /> Lagret</span> : <span className="text-[11px] text-muted-foreground">Ikke lagt inn</span>}
      </div>
      <div className="relative">
        <Input id={id} type={show ? "text" : "password"} autoComplete="off" spellCheck={false} value={value} onChange={onChange} placeholder={isSet ? "•••••••• (la stå tom for å beholde)" : ""} className="pr-10 font-mono text-[13px]" data-testid={testId} />
        <button type="button" onClick={() => setShow((s) => !s)} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" aria-label={show ? "Skjul" : "Vis"} data-testid={`${testId}-toggle`}>{show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
      </div>
      {hint ? <p className="text-[11px] text-muted-foreground">{hint}</p> : null}
    </div>
  );
};

const StepRow = ({ done, label, detail, testId }) => (
  <li className="flex items-start gap-3" data-testid={testId}>
    <span className={cn("mt-0.5 h-5 w-5 rounded-full border flex items-center justify-center shrink-0", done ? "status-betalt" : "bg-background text-muted-foreground")}>{done ? <CheckCircle2 className="h-3.5 w-3.5" /> : <span className="h-1.5 w-1.5 rounded-full bg-current" />}</span>
    <div className="min-w-0"><p className={cn("text-sm font-medium", !done && "text-muted-foreground")}>{label}</p>{detail ? <p className="text-xs text-muted-foreground">{detail}</p> : null}</div>
  </li>
);

export default function PaymentSettingsPage() {
  const { activeWorkspace } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");
  const [advanced, setAdvanced] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const apply = (data) => {
    setCfg(data);
    setForm({ enabled: !!data.enabled, environment: data.environment || "test", client_id: "", client_secret: "", subscription_key: "", msn: data.msn || "", api_base_url_override: data.api_base_url_override || "" });
    if (data.api_base_url_override) setAdvanced(true);
  };

  const load = async () => {
    try {
      const r = await api.get("/sales/payments/vipps/config");
      apply(r.data);
    } catch (e) {
      setError(errorMessage(e));
    }
  };
  useEffect(() => { setCfg(null); load(); }, [activeWorkspace?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const run = async (key, fn, okMsg) => {
    setBusy(key);
    try {
      const res = await fn();
      if (okMsg) toast.success(typeof okMsg === "function" ? okMsg(res) : okMsg);
      return res;
    } catch (e) {
      toast.error(errorMessage(e));
      return null;
    } finally {
      setBusy("");
    }
  };

  const save = (overrides = {}) => run("save", async () => {
    const payload = { enabled: form.enabled, environment: form.environment, msn: form.msn || "", api_base_url_override: form.environment === "test" ? (form.api_base_url_override || "") : "", ...overrides };
    for (const k of ["client_id", "client_secret", "subscription_key"]) if (form[k]) payload[k] = form[k];
    const r = await api.put("/sales/payments/vipps/config", payload);
    apply(r.data);
    return r.data;
  }, "Vipps-oppsettet er lagret");

  const test = () => run("test", async () => {
    // Lagre eventuelle nye nøkler først, slik at testen bruker det som står i skjemaet
    if (form.client_id || form.client_secret || form.subscription_key || form.msn !== (cfg?.msn || "") || form.environment !== cfg?.environment) {
      const payload = { environment: form.environment, msn: form.msn || "", api_base_url_override: form.environment === "test" ? (form.api_base_url_override || "") : "" };
      for (const k of ["client_id", "client_secret", "subscription_key"]) if (form[k]) payload[k] = form[k];
      const r0 = await api.put("/sales/payments/vipps/config", payload);
      apply(r0.data);
    }
    const r = await api.post("/sales/payments/vipps/config/test");
    await load();
    return r.data;
  }, (r) => r.message || "Tilkobling OK");

  const registerWebhook = () => run("webhook", async () => {
    const r = await api.post("/sales/payments/vipps/config/webhook");
    await load();
    return r.data;
  }, "Webhook registrert hos Vipps");

  const toggleEnabled = (v) => run("enable", async () => {
    const r = await api.put("/sales/payments/vipps/config", { enabled: v });
    apply(r.data);
    return r.data;
  }, v ? "Vipps er slått på – betalingslenke legges på nye fakturaer og e-poster" : "Vipps er slått av");

  const remove = () => run("delete", async () => {
    await api.delete("/sales/payments/vipps/config");
    setConfirmDelete(false);
    await load();
  }, "Vipps-oppsettet er fjernet");

  const copyWebhook = async () => { if (cfg?.webhook_url && (await copyText(cfg.webhook_url))) toast.success("Webhook-adressen er kopiert"); };

  if (error && !cfg) return <Alert variant="destructive"><AlertTitle>Kunne ikke laste Vipps-oppsettet</AlertTitle><AlertDescription>{error}</AlertDescription></Alert>;
  if (!cfg) return <div className="space-y-4"><Skeleton className="h-6 w-64" /><Skeleton className="h-10 w-96" /><div className="grid gap-6 lg:grid-cols-[1fr_360px]"><Skeleton className="h-[520px]" /><Skeleton className="h-80" /></div></div>;

  const keysSaved = cfg.client_id_set && cfg.client_secret_set && cfg.subscription_key_set && !!cfg.msn;
  const isTest = form.environment === "test";
  const httpsOk = (cfg.app_url || "").startsWith("https://");
  const canEnable = keysSaved;

  return (
    <div className="space-y-6">
      <Breadcrumb items={[{ label: "Faktura", to: "/faktura" }, { label: "Betaling med Vipps" }]} />
      <PageHeader
        eyebrow={activeWorkspace?.name ? `Virksomhet: ${activeWorkspace.name}` : undefined}
        title="Betaling med Vipps og kort"
        description="La kundene betale fakturaen med ett trykk. Lenke og QR-kode legges automatisk på faktura-PDF, purringer og e-poster, og fakturaen markeres som betalt når Vipps bekrefter beløpet."
        actions={
          <div className="flex items-center gap-3 rounded-lg border bg-card px-3 py-2" data-testid="vipps-enabled-row">
            <div className="text-right whitespace-nowrap"><p className="text-sm font-medium">{cfg.ready ? "Vipps er på" : "Vipps er av"}</p><p className="text-[11px] text-muted-foreground">{canEnable ? "Gjelder nye PDF-er og e-poster" : "Lagre nøklene først"}</p></div>
            <Switch checked={!!cfg.enabled} disabled={!!busy || (!cfg.enabled && !canEnable)} onCheckedChange={toggleEnabled} data-testid="vipps-enabled-switch" />
          </div>
        }
      />

      {cfg.last_error ? (
        <Alert variant="destructive" data-testid="vipps-last-error"><AlertTriangle className="h-4 w-4" /><AlertTitle>Siste tilkobling feilet</AlertTitle><AlertDescription>{cfg.last_error}</AlertDescription></Alert>
      ) : null}
      {cfg.ready && cfg.environment === "test" ? (
        <Alert className="border-[#FFE2A3] dark:border-[#8A5A00]/50" data-testid="vipps-test-mode-alert"><Info className="h-4 w-4" /><AlertTitle>Testmiljø er aktivt</AlertTitle><AlertDescription>Betalinger går mot Vipps’ testmiljø (MT) og trekker ikke ekte penger. Kortbetaling er ikke tilgjengelig i test. Bytt til «Produksjon» og legg inn produksjonsnøklene før du fakturerer ekte kunder.</AlertDescription></Alert>
      ) : null}

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_360px] items-start">
        <div className="space-y-6 min-w-0">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2"><KeyRound className="h-4 w-4 text-muted-foreground" /> API-nøkler fra Vipps MobilePay-portalen</CardTitle>
              <CardDescription>Logg inn på portal.vippsmobilepay.com → Utvikler → velg salgsenheten (MSN) → «Vis nøkler». Nøklene lagres kryptert per virksomhet og vises aldri igjen i klartekst.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>Miljø</Label>
                  <Select value={form.environment} onValueChange={set("environment")}>
                    <SelectTrigger data-testid="vipps-environment-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-card">{VIPPS_ENVIRONMENTS.map((e) => <SelectItem key={e.value} value={e.value} data-testid={`vipps-environment-${e.value}`}>{e.label}</SelectItem>)}</SelectContent>
                  </Select>
                  <p className="text-[11px] text-muted-foreground">{isTest ? "apitest.vipps.no – bruk testnøkler fra MT-portalen." : "api.vipps.no – ekte betalinger."}</p>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="vp-msn">Merchant Serial Number (MSN)</Label>
                  <Input id="vp-msn" inputMode="numeric" placeholder="f.eks. 123456" value={form.msn} onChange={set("msn")} className="font-mono" data-testid="vipps-msn-input" />
                  <p className="text-[11px] text-muted-foreground">Salgsenhetens nummer, 4–10 siffer.</p>
                </div>
              </div>
              <SecretInput id="vp-cid" label="Client ID" value={form.client_id} onChange={set("client_id")} isSet={cfg.client_id_set} testId="vipps-client-id-input" />
              <SecretInput id="vp-csec" label="Client Secret" value={form.client_secret} onChange={set("client_secret")} isSet={cfg.client_secret_set} testId="vipps-client-secret-input" />
              <SecretInput id="vp-sub" label="Subscription Key (Ocp-Apim-Subscription-Key)" value={form.subscription_key} onChange={set("subscription_key")} isSet={cfg.subscription_key_set} hint="Primær eller sekundær nøkkel – begge fungerer." testId="vipps-subscription-key-input" />

              {isTest ? (
                <Collapsible open={advanced} onOpenChange={setAdvanced}>
                  <CollapsibleTrigger asChild>
                    <button type="button" className="flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground" data-testid="vipps-advanced-toggle"><ChevronDown className={cn("h-3.5 w-3.5 transition-transform", advanced && "rotate-180")} /> Avansert (kun test)</button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="pt-3">
                    <div className="space-y-1.5"><Label htmlFor="vp-override">Egen API-adresse (mock/simulator)</Label><Input id="vp-override" placeholder="https://…" value={form.api_base_url_override} onChange={set("api_base_url_override")} className="font-mono text-[13px]" data-testid="vipps-api-override-input" /><p className="text-[11px] text-muted-foreground">Overstyrer apitest.vipps.no. La stå tom i vanlig bruk.</p></div>
                  </CollapsibleContent>
                </Collapsible>
              ) : null}

              <div className="flex flex-wrap gap-2 pt-1">
                <Button onClick={() => save()} disabled={!!busy} data-testid="vipps-save-button">{busy === "save" ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle2 className="h-4 w-4 mr-2" />} Lagre nøkler</Button>
                <Button variant="outline" onClick={test} disabled={!!busy || (!keysSaved && !(form.client_id && form.client_secret && form.subscription_key && form.msn))} data-testid="vipps-test-button">{busy === "test" ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <PlugZap className="h-4 w-4 mr-2" />} Test tilkobling</Button>
                {keysSaved ? <Button variant="ghost" className="text-muted-foreground hover:text-destructive" onClick={() => setConfirmDelete(true)} disabled={!!busy} data-testid="vipps-delete-button"><Trash2 className="h-4 w-4 mr-2" /> Fjern oppsett</Button> : null}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2"><Webhook className="h-4 w-4 text-muted-foreground" /> Automatisk «Betalt» via webhook</CardTitle>
              <CardDescription>Vipps varsler Controlle i sanntid når kunden har betalt, og fakturaen markeres som betalt uten at du gjør noe. Som sikkerhetsnett sjekker Controlle også åpne betalinger mot Vipps hvert 2. minutt.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="rounded-lg border bg-secondary px-3 py-2.5 space-y-1">
                <p className="text-xs text-muted-foreground">Webhook-adresse (registreres hos Vipps for denne virksomheten)</p>
                <div className="flex items-center gap-2">
                  <code className="text-[12px] break-all flex-1" data-testid="vipps-webhook-url">{cfg.webhook_url || "–"}</code>
                  {cfg.webhook_url ? <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={copyWebhook} aria-label="Kopier webhook-adresse" data-testid="vipps-webhook-copy"><Copy className="h-3.5 w-3.5" /></Button> : null}
                </div>
              </div>
              {!httpsOk ? (
                <Alert data-testid="vipps-https-alert"><Info className="h-4 w-4" /><AlertTitle>Krever offentlig https-adresse</AlertTitle><AlertDescription>Webhooks kan først registreres når appen kjører på en offentlig https-adresse. Administrator setter adressen under Systeminnstillinger → «Lenke til appen». {cfg.app_url_source === "request" ? "Nå brukes adressen du er inne på." : ""}</AlertDescription></Alert>
              ) : null}
              <div className="flex flex-wrap items-center gap-2">
                <Button variant={cfg.webhook_id ? "outline" : "default"} onClick={registerWebhook} disabled={!!busy || !keysSaved || !httpsOk} data-testid="vipps-webhook-button">{busy === "webhook" ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Webhook className="h-4 w-4 mr-2" />} {cfg.webhook_id ? "Registrer på nytt" : "Registrer webhook hos Vipps"}</Button>
                {cfg.webhook_id ? <Badge variant="outline" className="status-betalt gap-1" data-testid="vipps-webhook-status"><CheckCircle2 className="h-3 w-3" /> Registrert {formatDateTime(cfg.webhook_registered_at)}</Badge> : <Badge variant="outline" className="status-avvist" data-testid="vipps-webhook-status">Ikke registrert</Badge>}
              </div>
              <p className="text-xs text-muted-foreground">Registrer på nytt hvis du bytter miljø, MSN eller appens adresse. Uten webhook fungerer betaling fortsatt – fakturaen markeres betalt av den periodiske sjekken og når kunden kommer tilbake til Controlle etter betaling.</p>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-4 lg:sticky lg:top-24 min-w-0">
          <Card>
            <CardHeader className="pb-3"><CardTitle className="text-base">Status</CardTitle></CardHeader>
            <CardContent>
              <ul className="space-y-3" data-testid="vipps-status-steps">
                <StepRow done={keysSaved} label="Nøkler og MSN lagret" detail={keysSaved ? `${cfg.environment === "prod" ? "Produksjon" : "Testmiljø"} · MSN ${cfg.msn}` : "Fyll inn de fire feltene og lagre."} testId="vipps-step-keys" />
                <StepRow done={!!cfg.verified_at} label="Tilkobling verifisert" detail={cfg.verified_at ? `Sist testet ${formatDateTime(cfg.verified_at)}` : "Trykk «Test tilkobling»."} testId="vipps-step-verified" />
                <StepRow done={!!cfg.webhook_id} label="Webhook registrert" detail={cfg.webhook_id ? "Sanntidsvarsel om betaling" : "Anbefalt for øyeblikkelig «Betalt»."} testId="vipps-step-webhook" />
                <StepRow done={!!cfg.ready} label="Vipps slått på" detail={cfg.ready ? "Betalingslenke legges på fakturaer" : "Bruk bryteren øverst."} testId="vipps-step-enabled" />
              </ul>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3"><CardTitle className="text-base">Slik fungerer det for kunden</CardTitle></CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="flex gap-3"><QrCode className="h-4 w-4 mt-0.5 text-primary shrink-0" /><p><span className="font-medium">Knapp og QR-kode</span> på faktura-PDF og purringer. <span className="text-muted-foreground">Lenken går til en betalingsside i Controlle.</span></p></div>
              <div className="flex gap-3"><Mail className="h-4 w-4 mt-0.5 text-primary shrink-0" /><p><span className="font-medium">«Betal med Vipps eller kort»</span> i faktura-e-posten.</p></div>
              <div className="flex gap-3"><Smartphone className="h-4 w-4 mt-0.5 text-primary shrink-0" /><p><span className="font-medium">Kunden velger Vipps eller kort</span>, betaler, og fakturaen blir <span className="font-medium">Betalt</span> automatisk når beløpet er bekreftet.</p></div>
              <p className="text-xs text-muted-foreground pt-1">Kontonummer og KID står fortsatt på fakturaen for dem som vil betale i nettbanken.</p>
            </CardContent>
          </Card>

          <p className="text-xs text-muted-foreground px-1">Trenger du å endre firmaopplysninger eller purreregler? <Link to="/profil#faktura" className="underline" data-testid="vipps-profile-link">Gå til fakturaoppsett</Link>.</p>
        </div>
      </div>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent className="bg-card" data-testid="vipps-delete-dialog">
          <AlertDialogHeader><AlertDialogTitle>Fjerne Vipps-oppsettet?</AlertDialogTitle><AlertDialogDescription>Nøklene slettes fra Controlle og webhooken avregistreres hos Vipps. Betalingslenker på allerede sendte fakturaer vil vise at Vipps ikke er tilgjengelig. Betalinger som er gjennomført påvirkes ikke.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel data-testid="vipps-delete-cancel">Avbryt</AlertDialogCancel><AlertDialogAction onClick={remove} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="vipps-delete-confirm">{busy === "delete" ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null} Fjern</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
